/* Certain "forms" are not wrapped in a standard <form> element but we still need to detect the
 * most relevant element wrapping a group of dangling input elements to infer its type.
 * This is notably the case on :
 * - apple.com : no forms but proprietary DOM tags <aid-create>
 * - adobe.com : input elements wrapped in a <section> tag */
import { clusters as clustering } from "@protontech/fathom";
import { inputCandidateSelector, kButtonSelector, kButtonSubmitSelector, kDomClusterSelector, kFieldSelector } from "../constants/selectors";
import { findStackedParents, getCommonAncestor, getRectMinDistance, pruneNested, uniqueNodes, walkUpWhile } from "./dom";
import { isBtnCandidate } from "./field";
import { isVisibleField } from "./visible";

import { isTopFrame } from "./exclusion";
import { flagCluster, isClassifiable } from "./flags";
import { selectFormCandidates } from "./form";
import { isIFrameField } from "./iframe";

const { clusters } = clustering;

const CLUSTER_MAX_X_DIST = 50;
const CLUSTER_MAX_Y_DIST = 275;
const CLUSTER_ALIGNMENT_TOLERANCE = 0.05;
const CLUSTER_MAX_ELEMENTS = 50;

type ElementData = { isField: boolean; rect: DOMRect };
const context = { cache: new WeakMap<HTMLElement, ElementData>() };

const getElementData = (el: HTMLElement) => {
  const data = context.cache.get(el) ?? {
    isField: el.matches(kFieldSelector) && el.matches(':not([type="submit"])'),
    rect: el.getBoundingClientRect(),
  };

  context.cache.set(el, data);
  return data;
};

/* Given two elements, the `compare` function will apply some heuristics
 * to decide wether or not the two elements belong to the same cluster :
 * - if aligned vertically & horizontally -> `true`
 * - if aligned vertically & min vertical dist between elements small -> `true`
 * - if aligned horizontally & min horizontal dist between elements small -> `true`
 * - if min horizontal & min vertical dists small -> `true`
 * decrease the `maxDy` value when comparing fields. The idea here
 * is that fields tend to be "closer" together - whereas submit buttons
 * may be more distant to the cluster's input fields */
const compare = (elA: HTMLElement, elB: HTMLElement) => {
  const a = getElementData(elA);
  const b = getElementData(elB);

  const maxDx = CLUSTER_MAX_X_DIST;
  const maxDy = CLUSTER_MAX_Y_DIST / (a.isField && b.isField ? 2 : 1);
  const { dx, dy } = getRectMinDistance(a.rect, b.rect);

  const leftRatio = Math.abs(a.rect.left / b.rect.left);
  const topRatio = Math.abs(a.rect.top / b.rect.top);
  const xAlign = leftRatio > 1 - CLUSTER_ALIGNMENT_TOLERANCE && leftRatio < 1 + CLUSTER_ALIGNMENT_TOLERANCE;
  const yAlign = topRatio > 1 - CLUSTER_ALIGNMENT_TOLERANCE && topRatio < 1 + CLUSTER_ALIGNMENT_TOLERANCE;

  if (xAlign && yAlign) return true;
  if (xAlign && dy < maxDy) return true;
  if (yAlign && dx < maxDx) return true;
  if (dx < maxDx && dy < maxDy) return true;

  return false;
};

const handleSingletonCluster = (cluster: HTMLElement[]) => {
  const node = cluster[0];
  return walkUpWhile(
    node,
    5,
  )((parent, candidate) => {
    /** Avoid resolving the top-level HTML tag which
     * would be "un-selectable" via DOM querying */
    if (parent.tagName === "HTML") return false;
    if (candidate === node) return true;
    return candidate.querySelectorAll(kButtonSelector).length === 0;
  });
};

export const resolveFormClusters = (doc: Document) => {
  /* check wether the matched elements belong to a form
   * element, if that is the case we can safely remove
   * them from the matched elements as we've most likely
   * already detected the parent form and won't need to.
   * Also filter out input elements that are not of interest
   * for any of our form detection models */
  const forms = selectFormCandidates(doc);
  const clusterable = (els: HTMLElement[]) => els.filter((el) => !forms.some((ex) => ex.contains(el)) && isVisibleField(el));
  const fields = Array.from(doc.querySelectorAll<HTMLElement>(kFieldSelector));
  const fieldsOfInterest = clusterable(fields.filter((el) => isClassifiable(el) && el.getAttribute("type") !== "hidden"));
  const inputs = fieldsOfInterest.filter((field) => field.matches(inputCandidateSelector));

  if (inputs.length === 0 || inputs.length > CLUSTER_MAX_ELEMENTS) return [];

  /* Common modal, dialog or overlays containing
   * at least one input field. Will be used to "out-distance"
   * elements which belong to an overlay dialog (ie: fixed position)
   * when computing the euclidean distance to a node located outside
   * of the overlay.
   * For each input element, check if it belongs to any positioned parent :
   * our `clusterSelector` may have missed these. The idea here is to include
   * in our `groups` list positioned nodes in order to avoid FPs when doing
   * distance checks on stacked elements */
  const domGroups = Array.from(doc.querySelectorAll<HTMLElement>(kDomClusterSelector)).filter((el) => el !== document.body);
  const positionedEls = findStackedParents(inputs, 20);
  const groups = pruneNested(domGroups.filter((el) => !positionedEls.some((stack) => el.contains(stack))).concat(positionedEls));
  const buttons = clusterable(Array.from(document.querySelectorAll<HTMLElement>(kButtonSubmitSelector)).filter(isBtnCandidate));
  const iframes = clusterable(Array.from(doc.querySelectorAll("iframe")).filter(isIFrameField));
  const candidates = uniqueNodes(fieldsOfInterest, buttons, iframes);

  if (candidates.length > CLUSTER_MAX_ELEMENTS) return [];

  /* clustering can be CPU intensive - if we go above `CLUSTER_MAX_ELEMENTS` elements
   * to cluster, early return. Most likely we're not interested in the clustering
   * result either way - if candidates have the same parent element : group them 
  /* if groups don't match then likely not in the same cluster */
  /* if both elements belong to the same group they must
   * belong to the same cluster */
  const groupByInput = new WeakMap(candidates.map((el) => [el, groups.find((group) => group.contains(el))]));

  const theClusters: HTMLElement[][] = clusters(candidates, 1, (a: HTMLElement, b: HTMLElement) => {
    if (a.parentElement === b.parentElement) return 0;

    const groupA = groupByInput.get(a);
    const groupB = groupByInput.get(b);
    if (groupA !== groupB) return Number.MAX_SAFE_INTEGER;
    if (groupA && groupA === groupB) return 0;

    return compare(a, b) ? 0 : Number.MAX_SAFE_INTEGER;
  });

  /* Resolve the closest and most common ancestor of the cluster's internal,
   * nodes we'll consider it the "form-like" element for this set of fields.
   * As we may resolve multiple clusters, prune nested clusters.
   * If a cluster has only one element : we may be dealing with a clustering
   * FN due to our distance heuristics, in this case resolve the node two
   * parents up as a heuristic  */
  const ancestors = theClusters
    .map((cluster) => (cluster.length === 1 ? handleSingletonCluster(cluster) : cluster.reduce(getCommonAncestor)))
    .filter((ancestor) => {
      /** Tolerate body tag as virtual form element in iframes */
      if (isTopFrame() && document.body === ancestor) return false;
      return ancestor.querySelectorAll(inputCandidateSelector).length > 0;
    });

  const result = pruneNested(ancestors);
  result.forEach(flagCluster);
  context.cache = new WeakMap();

  return result;
};
